import {
  useEasyServiceGetApiEasyAdministrationArea,
  useLineItemApiServiceGetApiLineItemApiLineItemProperties,
  useLineItemApiServicePutApiLineItemApiLineItemProperties,
} from "../../openapi/api/queries/queries";
import CopyToClipboard from "../CopyToClipboard/CopyToClipboard";
import { ErrorType } from "../CustomCartIdRouteComponent/CustomCartIdRouteComponent";
import {
  cartStore,
  updateSelectedItems,
} from "../CustomCartIdRouteComponent/storeCart";
import { TextFieldSimple } from "../MuiWrappers/TextFieldSimple";
import PriceIndicator from "../PriceIndicator/PriceIndicator";

import { useCallback, useEffect, useMemo, useState } from "react";
import { JSX } from "react";
import { useTranslation } from "react-i18next";

//import { isLineItem, isSubItem } from "../Table/helper/helper";

import {
  IconButtonLink,
  StandardComponents,
  VMItemGroups,
  VMLineItems,
  VMQuotes,
} from "@4pace-cpq/core";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Checkbox,
  TableCell,
  ThemeProvider,
  Tooltip,
  Typography,
  debounce,
} from "@mui/material";
import { useSearch } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";

import { theme as CustomLineItemTheme } from "./CustomLineItemTheme";

export interface ISyncSelect {
  configId: string;
  checked: boolean;
}
interface ICustomLineItem {
  item: VMLineItems;
  quoteId: VMQuotes["id"];
  itemGroupId: VMItemGroups["id"];
  idx: number;
  error?: ErrorType;
  errorList?: ErrorType[];
  isLoggedIntoEasy: boolean;
}

export function CustomLineItem({
  item,
  quoteId,
  itemGroupId,
  idx,
  error,
  errorList,
  isLoggedIntoEasy,
}: ICustomLineItem): JSX.Element {
  const { t } = useTranslation();
  const { data: lineItemProperties } =
    useLineItemApiServiceGetApiLineItemApiLineItemProperties({
      lineItemId: item.id,
    });
  const [notes, setNotes] = useState<string>(lineItemProperties?.notes || "");
  const [customerId, setCustomerId] = useState<string>(
    lineItemProperties?.customerId || "",
  );
  const checked = useStore(cartStore, (state) =>
    state.selectedItems.includes(item.id),
  );
  const { data: administrationAreaData } =
    useEasyServiceGetApiEasyAdministrationArea();

  const { mutate: setLineItemProperties } =
    useLineItemApiServicePutApiLineItemApiLineItemProperties();

  const { productCategory } = useSearch({ strict: false });

  const search = {
    quoteId,
    itemGroupId,
    lineItemId: item.id,
    cfgObjectId: item.product?.cfgObjectIdentifier,
    configurationId: item.configurationId,
    productCategory,
    administrationArea: administrationAreaData?.string,
    notes,
    customerId,
    systemEnvironment: import.meta.env.MODE,
  };

  const handleSyncSelect = useCallback((): void => {
    updateSelectedItems(item.id);
  }, [item.id]);

  useEffect(() => {
    if (lineItemProperties?.notes) {
      setNotes(lineItemProperties?.notes);
    }
    if (lineItemProperties?.customerId) {
      setCustomerId(lineItemProperties?.customerId);
    }
  }, [lineItemProperties]);

  const sendNotesChange = useCallback(
    (value: string) => {
      setLineItemProperties({
        lineItemId: item.id,
        requestBody: {
          notes: value,
        },
      });
    },
    [item.id, setLineItemProperties],
  );

  const sendCustomerIdChange = useCallback(
    (value: string) => {
      setLineItemProperties({
        lineItemId: item.id,
        requestBody: {
          customerId: value,
        },
      });
    },
    [item.id, setLineItemProperties],
  );

  const debouncedNotesChange = useMemo(() => {
    return debounce(sendNotesChange, 500);
  }, [sendNotesChange]);

  const debouncedCustomerIdChange = useMemo(() => {
    return debounce(sendCustomerIdChange, 500);
  }, [sendCustomerIdChange]);

  function handleNotesChange(value: string): void {
    setNotes(value);
    debouncedNotesChange(value);
  }

  function handleCustomerIdChange(value: string): void {
    setCustomerId(value);
    debouncedCustomerIdChange(value);
  }

  return (
    <ThemeProvider theme={CustomLineItemTheme}>
      {errorList && errorList.length > 0 && (
        <TableCell component="th" scope="row" align="right">
          {error?.exceptionMessage && error?.exceptionMessage !== "" && (
            <Tooltip title={error?.exceptionMessage}>
              <WarningIcon color="warning" />
            </Tooltip>
          )}
        </TableCell>
      )}
      {isLoggedIntoEasy && (
        <TableCell component="th" scope="row" align="right">
          {<Checkbox checked={checked} onChange={handleSyncSelect} />}
        </TableCell>
      )}
      <TableCell component="th" scope="row" align="center">
        {idx + 1}
      </TableCell>
      <TableCell align="left">
        <Box display="flex" alignItems="center">
          <Tooltip title={item.id}>
            <Typography variant="body2" noWrap maxWidth={"4em"}>
              {item.id}
            </Typography>
          </Tooltip>
          <CopyToClipboard data={item.id?.toString()} />
        </Box>
      </TableCell>
      <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
        {lineItemProperties?.typeCode}
      </TableCell>
      <TableCell
        align="center"
        sx={{ ">.MuiFormControl-root": { width: "4rem" } }}
      >
        <StandardComponents.ConnectedQuantity
          item={item}
          value={item.quantity || 1}
          allowDecimal={false}
        />
      </TableCell>
      <TableCell align="left">
        <TextFieldSimple
          value={notes}
          onChange={(e) => {
            handleNotesChange(e.target.value);
          }}
        />
      </TableCell>
      <TableCell align="left">
        <TextFieldSimple
          value={customerId}
          onChange={(e) => {
            handleCustomerIdChange(e.target.value);
          }}
          sx={{ maxWidth: "6rem" }}
        />
      </TableCell>
      <TableCell align="left">
        <PriceIndicator price={Number(lineItemProperties?.priceIndicator)} />
      </TableCell>
      {/* {isLoggedIntoEasy && <TableCell align="left">Completed</TableCell>} */}
      <TableCell align="right">
        <Box display="flex">
          <Tooltip title={t("WEG:buttons.edit")}>
            <IconButtonLink to="/configurator" search={search}>
              <EditIcon
                color={item.configuration?.isComplete ? "success" : "warning"}
              />
            </IconButtonLink>
          </Tooltip>
          <StandardComponents.CloneItemIconButton item={item} />
          <StandardComponents.DeleteItemIconButton item={item} />
        </Box>
      </TableCell>
    </ThemeProvider>
  );
}
